/**
 * BuchhaltungDashboard — Finanzen genel bakış.
 * KPI'lar + OPOS listesi + ERP fatura köprüsü (idempotent içe aktarma).
 * Mobil öncelikli: kartlar tek sütuna düşer, dokunma hedefleri 44px.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import AbgleichsquoteCard from '@/components/bank/AbgleichsquoteCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Euro, TrendingUp, TrendingDown, Landmark, FileInput, Loader2,
  CircleDollarSign, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/api/AuthContext';
import {
  listAccounts, fetchAccountTotals, buildGuV, listOpenItems,
  findUnbookedInvoices, bookInvoiceFromOrder, recordPayment,
  fmtEUR,
} from '@/lib/buchhaltungApi';
const year = new Date().getFullYear();
const RANGE = { dateFrom: `${year}-01-01`, dateTo: `${year}-12-31` };

export default function BuchhaltungDashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [payingId, setPayingId] = useState(null);

  const { data: accounts = [] } = useQuery({ queryKey: ['fibu-accounts'], queryFn: () => listAccounts() });
  const { data: totals } = useQuery({
    queryKey: ['fibu-totals', RANGE],
    queryFn: () => fetchAccountTotals(RANGE),
  });
  const { data: openItems = [] } = useQuery({ queryKey: ['fibu-open-items'], queryFn: () => listOpenItems() });
  const { data: orders = [] } = useQuery({ queryKey: ['orders'], queryFn: () => base44.entities.Order.list() });
  const { data: unbooked = [] } = useQuery({
    queryKey: ['fibu-unbooked', orders.length],
    queryFn: () => findUnbookedInvoices(orders),
    enabled: orders.length > 0,
  });

  const guv = useMemo(
    () => (totals ? buildGuV(accounts, totals) : { revenue: 0, expense: 0, result: 0 }),
    [accounts, totals]
  );

  const receivables = openItems.filter((i) => i.type === 'receivable');
  const openSum = receivables.reduce((s, i) => s + Number(i.open_amount), 0);
  const today = new Date().toISOString().slice(0, 10);
  const overdue = receivables.filter((i) => i.due_date && i.due_date < today);

  const importMutation = useMutation({
    mutationFn: async () => {
      let ok = 0; const errors = [];
      for (const order of unbooked) {
        try { await bookInvoiceFromOrder(order, user?.email); ok++; }
        catch (err) { errors.push(`${order.invoice_number}: ${err.message}`); }
      }
      return { ok, errors };
    },
    onSuccess: ({ ok, errors }) => {
      queryClient.invalidateQueries();
      if (ok > 0) toast.success(`${ok} Rechnung(en) gebucht — Debitor an Erlöse + USt, OPOS angelegt.`);
      errors.forEach((e) => toast.warning(e));
      if (ok === 0 && errors.length === 0) toast.info('Keine offenen Rechnungen zum Buchen.');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  const payMutation = useMutation({
    mutationFn: async (item) => recordPayment({
      openItem: item,
      amount: Number(item.open_amount),
      paymentDate: today,
      actorEmail: user?.email,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries();
      toast.success('Zahlung gebucht — Bank an Debitor, OP ausgeglichen.');
      setPayingId(null);
    },
    onError: (err) => { toast.error('Fehler: ' + err.message); setPayingId(null); },
  });

  const kpis = [
    { label: `Erlöse ${year}`, value: fmtEUR(guv.revenue), icon: TrendingUp, color: 'text-emerald-600 bg-emerald-50' },
    { label: `Aufwand ${year}`, value: fmtEUR(guv.expense), icon: TrendingDown, color: 'text-red-600 bg-red-50' },
    { label: 'Vorl. Ergebnis', value: fmtEUR(guv.result), icon: Euro, color: guv.result >= 0 ? 'text-emerald-600 bg-emerald-50' : 'text-red-600 bg-red-50' },
    { label: 'Offene Forderungen', value: fmtEUR(openSum), icon: CircleDollarSign, color: 'text-amber-600 bg-amber-50', sub: overdue.length > 0 ? `${overdue.length} überfällig` : null },
  ];

  return (
    <PageShell
      head={(
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <PageHeader icon={<Landmark className="w-5 h-5" />} title="Finanzen" />
              <Badge variant="outline" className="text-[10px]">SKR04 · {year}</Badge>
            </div>
            {unbooked.length > 0 && (
              <Button
                onClick={() => importMutation.mutate()}
                disabled={importMutation.isPending}
                className="h-11 bg-slate-900 hover:bg-slate-800 text-white gap-2 text-xs font-bold"
              >
                {importMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileInput className="w-4 h-4" />}
                {unbooked.length} Rechnung(en) buchen
              </Button>
            )}
          </div>
      )}
    >

      {/* KPI'lar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map((k, i) => (
          <Card key={i}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2.5">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${k.color}`}>
                  <k.icon className="w-4.5 h-4.5 w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm sm:text-lg font-black text-slate-900 truncate">{k.value}</p>
                  <p className="text-[10px] text-slate-500 truncate">{k.label}</p>
                  {k.sub && <p className="text-[10px] font-bold text-red-600">{k.sub}</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <AbgleichsquoteCard />

      {/* OPOS — Offene Posten */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <CircleDollarSign className="w-4 h-4 text-amber-600" />
            Offene Posten (Debitoren)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {receivables.length === 0 && (
            <div className="py-8 text-center text-slate-400">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-2 opacity-20 text-emerald-500" />
              <p className="text-sm">Keine offenen Posten.</p>
              <p className="text-xs">Neue Rechnungen erscheinen hier nach dem Buchen.</p>
            </div>
          )}
          {receivables.map((item) => {
            const isOverdue = item.due_date && item.due_date < today;
            return (
              <div
                key={item.id}
                className="flex items-center justify-between gap-2 p-3 bg-white border border-slate-200 rounded-lg"
              >
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-800 truncate">
                    {item.invoice_no} · {item.partner_name || '—'}
                  </p>
                  <p className={`text-[10px] ${isOverdue ? 'text-red-600 font-bold' : 'text-slate-500'}`}>
                    {isOverdue && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                    Fällig: {item.due_date ? new Date(item.due_date).toLocaleDateString('de-DE') : '—'}
                    {item.status === 'partial' ? ' · Teilzahlung' : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <p className="text-sm font-black text-slate-900">{fmtEUR(item.open_amount)}</p>
                  {/* Mahnwesen artık otomatik kademe motorunda (Mahnungen
                      sayfası) — buradan yalnızca yönlendirme + kademe rozeti. */}
                  {isOverdue && (
                    <Link
                      to="/buchhaltung/mahnungen"
                      className="inline-flex items-center h-9 px-2.5 rounded-md border border-amber-200 bg-amber-50 text-amber-700 text-[10px] font-bold hover:bg-amber-100 transition-colors"
                    >
                      Mahnwesen{item.dunning_level > 0 ? ` · M${item.dunning_level}` : ''}
                    </Link>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-9 text-[10px] font-bold text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                    disabled={payMutation.isPending && payingId === item.id}
                    onClick={() => {
                      if (window.confirm(`Zahlungseingang ${fmtEUR(item.open_amount)} für ${item.invoice_no} buchen?`)) {
                        setPayingId(item.id);
                        payMutation.mutate(item);
                      }
                    }}
                  >
                    {payMutation.isPending && payingId === item.id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : 'Bezahlt'}
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </PageShell>
  );
}
